import * as React from 'react';
import { TooltipHost } from '@fluentui/react';
import { SubscriptionStatusEnum } from '../data-structures/enums';
import { LanguageContext } from '../utilities/LocalizationModule';
import { Pill, Tone } from './Pill';

interface Props {
    // Stringified status as returned by the API (capitalized: "Active", "Canceled", "Suspended", "Pending", "Completed", or "pending"/lowercase variants).
    status: string;
    // Local-side scheduled cancellation timestamp (Stripe). When set in the future, the pill flips to "Cancels DATE" regardless of status.
    cancelAtRaw?: Date | null;
    // Period-end timestamp. Used as the "cancels on" date for non-Stripe rows whose Status is already Canceled but expiry is in the future.
    expiryDateRaw?: Date | null;
}

// "21 Jun" when the date is in the current year, "25 May 2027" otherwise.
function formatCancelsOn(date: Date): string {
    const now = new Date();
    const sameYear = date.getFullYear() === now.getFullYear();
    const month = date.toLocaleString('en-US', { month: 'short' });
    return sameYear
        ? `${date.getDate()} ${month}`
        : `${date.getDate()} ${month} ${date.getFullYear()}`;
}

function resolveFutureCancelDate(status: string, cancelAtRaw?: Date | null, expiryDateRaw?: Date | null): Date | null {
    const now = new Date();
    const statusKey = (status || '').toLowerCase();

    // (A) Stripe future-cancellation: CancelAt is set, sub is still Active. We gate on
    //     Status === Active because a stale CancelAt can linger on rows that are already
    //     terminal: ProjectDeletedSubscriptionAsync and the missing-item-detection path
    //     don't clear CancelAt when they flip Status to Canceled, so without the guard
    //     an already-canceled row could falsely render "Cancels FUTURE DATE".
    if (statusKey === SubscriptionStatusEnum.Active && cancelAtRaw && cancelAtRaw > now) {
        return cancelAtRaw;
    }

    // (B) Non-Stripe / invoice flow: row flipped to Canceled with a future ExpiryDate.
    //     Existing portal logic already treats this as "still has access until expiry".
    if (statusKey === SubscriptionStatusEnum.Canceled && expiryDateRaw && expiryDateRaw > now) {
        return expiryDateRaw;
    }

    return null;
}

const SubscriptionStatusPill: React.FC<Props> = ({ status, cancelAtRaw, expiryDateRaw }) => {
    const languageStrings = React.useContext(LanguageContext);
    const futureCancel = resolveFutureCancelDate(status, cancelAtRaw, expiryDateRaw);

    if (futureCancel) {
        const cancelsTemplate: string = (languageStrings as any).CancelsOn || 'Cancels {date}';
        const label = cancelsTemplate.replace('{date}', formatCancelsOn(futureCancel));
        const tooltip = `${label} (${futureCancel.toLocaleString()})`;
        return (
            <TooltipHost content={tooltip}>
                <Pill tone="info" iconName="Clock" role="status" ariaLabel={tooltip}>
                    {label}
                </Pill>
            </TooltipHost>
        );
    }

    const statusKey = (status || '').toLowerCase();
    let tone: Tone = 'neutral';
    let displayKey: string | null = null;
    switch (statusKey) {
        case SubscriptionStatusEnum.Active:
            tone = 'success';
            displayKey = 'Active';
            break;
        case SubscriptionStatusEnum.Suspended:
            tone = 'warning';
            // No localization key in LocalizationModule today; fall through to raw display.
            displayKey = null;
            break;
        case SubscriptionStatusEnum.Canceled:
            tone = 'danger';
            displayKey = 'Canceled';
            break;
        case SubscriptionStatusEnum.Pending:
            tone = 'info';
            displayKey = 'Pending';
            break;
        case SubscriptionStatusEnum.Completed:
        default:
            tone = 'neutral';
            displayKey = null;
            break;
    }

    // Prefer a localized string when we have one. Otherwise fall back to first-letter
    // capitalization of whatever the API returned so a previously-unhandled status
    // (e.g., a new Stripe value) is still rendered.
    const localized = displayKey ? (languageStrings as any)[displayKey] : null;
    const display = localized
        ? localized
        : status
            ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
            : '—';

    return (
        <Pill tone={tone} role="status" ariaLabel={display}>
            {display}
        </Pill>
    );
};

export default SubscriptionStatusPill;
